import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  User,
  CreditCard,
  Settings,
  Crown,
  Clock,
  CheckCircle,
  ArrowRight,
  Trophy,
  BarChart3,
  Heart,
  GraduationCap,
  ChevronRight,
  ChevronDown,
  X,
  Check,
  RotateCcw,
  Target,
  TrendingUp,
  AlertCircle,
  Dna,
  Beaker,
  Atom,
  Sparkles,
  Play,
  Trash2,
  Eye,
  Gift
} from 'lucide-react';
import { api } from '../lib/api';
import { resultsApi } from '../lib/results';
import { questionsApi } from '../lib/questions';

const SUPABASE_URL = 'https://ffccmyllnijevswabqhr.supabase.co';

const getImageUrl = (imageName) => {
  if (!imageName) return null;
  const isPng = imageName.startsWith('i') ||
                imageName.startsWith('7B') ||
                imageName.startsWith('7CH') ||
                imageName.startsWith('9CH');
  const ext = isPng ? 'png' : 'jpg';
  return `${SUPABASE_URL}/storage/v1/object/public/question-images/${imageName}.${ext}`;
};

const Dashboard = () => {
  const { user, subscription, updatePassword, checkSubscription } = useAuth();
  const navigate = useNavigate();

  const [activeCard, setActiveCard] = useState(null);
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState(null);
  const [favorites, setFavorites] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [practiceQuestions, setPracticeQuestions] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [timeFilter, setTimeFilter] = useState('all'); // 'all', '7days', '30days', '90days'

  // Promo code state
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const [promoMessage, setPromoMessage] = useState({ type: '', text: '' });

  const hasActiveSubscription = subscription?.hasSubscription;

  const PLAN_LABELS = {
    '1_month': { name: 'Synapso Komplet', detail: '1 měsíc', color: 'text-primary-400' },
    '3_months': { name: 'Synapso Komplet', detail: '3 měsíce', color: 'text-primary-400' },
    '6_months': { name: 'Synapso Komplet', detail: '6 měsíců', color: 'text-primary-400' },
    'bio_1_month': { name: 'Synapso Biologie', detail: '1 měsíc', color: 'text-green-400' },
    'phy_1_month': { name: 'Synapso Fyzika', detail: '1 měsíc', color: 'text-blue-400' },
    'che_1_month': { name: 'Synapso Chemie', detail: '1 měsíc', color: 'text-yellow-400' },
  };

  // Filtrování podle času
  const filterByTime = (items) => {
    if (timeFilter === 'all') return items;

    const now = new Date();
    const daysMap = { '7days': 7, '30days': 30, '90days': 90 };
    const days = daysMap[timeFilter];
    const cutoffDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);

    return items.filter(item => new Date(item.created_at) >= cutoffDate);
  };

  const filteredResults = filterByTime(results);
  const filteredSimulations = filterByTime(simulations);

  // Výpočet statistik z filtrovaných dat
  const calculateFilteredStats = (filteredData) => {
    const allFiltered = [...filteredData, ...filteredSimulations];

    if (allFiltered.length === 0) {
      return {
        totalTests: 0,
        totalQuestions: 0,
        totalCorrect: 0,
        averagePercentage: 0,
        totalTime: 0,
        byCategory: {},
        weakCategories: [],
        strongCategories: []
      };
    }

    const totalTests = allFiltered.length;
    const totalQuestions = allFiltered.reduce((sum, r) => sum + r.total_questions, 0);
    const totalCorrect = allFiltered.reduce((sum, r) => sum + r.score, 0);
    const averagePercentage = Math.round(totalCorrect / totalQuestions * 100);
    const totalTime = allFiltered.reduce((sum, r) => sum + (r.time_seconds || 0), 0);

    // Statistiky podle kategorie
    const byCategory = {};
    allFiltered.forEach(result => {
      const cat = result.category || 'Simulace';
      if (!byCategory[cat]) {
        byCategory[cat] = { tests: 0, correct: 0, total: 0, totalTime: 0 };
      }
      byCategory[cat].tests += 1;
      byCategory[cat].correct += result.score;
      byCategory[cat].total += result.total_questions;
      byCategory[cat].totalTime += result.time_seconds || 0;
    });

    // Vypočítat průměr pro každou kategorii
    Object.keys(byCategory).forEach(cat => {
      byCategory[cat].percentage = Math.round(byCategory[cat].correct / byCategory[cat].total * 100);
    });

    // Seřadit kategorie podle úspěšnosti
    const sortedCategories = Object.entries(byCategory)
      .map(([name, stats]) => ({ name, ...stats }))
      .sort((a, b) => a.percentage - b.percentage);

    const weakCategories = sortedCategories.filter(c => c.percentage < 70).slice(0, 3);
    const strongCategories = sortedCategories.filter(c => c.percentage >= 70).sort((a, b) => b.percentage - a.percentage).slice(0, 3);

    return {
      totalTests,
      totalQuestions,
      totalCorrect,
      averagePercentage,
      totalTime,
      byCategory,
      weakCategories,
      strongCategories
    };
  };

  const filteredStats = calculateFilteredStats(filteredResults);

  useEffect(() => {
    if (user && hasActiveSubscription) {
      loadData();
    }
  }, [user, hasActiveSubscription]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resultsData, statsData, favoritesData] = await Promise.all([
        resultsApi.getUserResults(user.id),
        resultsApi.getUserStats(user.id),
        resultsApi.getFavorites(user.id)
      ]);
      setResults(resultsData.filter(r => r.type === 'quiz'));
      setSimulations(resultsData.filter(r => r.type === 'simulation'));
      setStats(statsData);
      setFavorites(favoritesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    const stripeCustomerId = subscription?.subscription?.stripe_customer_id;
    console.log('handleManageSubscription called, stripe_customer_id:', stripeCustomerId);

    try {
      const { url, error } = await api.createPortalSession(stripeCustomerId, user?.email);
      if (url) {
        window.location.href = url;
      } else {
        console.error('Portal session error:', error);
        alert('Nepodařilo se otevřít správu předplatného. Kontaktujte podporu na info@synapso.cz');
      }
    } catch (error) {
      console.error('Error creating portal session:', error);
      alert('Nepodařilo se otevřít správu předplatného. Kontaktujte podporu na info@synapso.cz');
    }
  };

  const formatTime = (seconds) => {
    if (!seconds) return '-';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'Biologie': return Dna;
      case 'Chemie': return Beaker;
      case 'Fyzika': return Atom;
      case 'Somatologie': return Heart;
      default: return GraduationCap;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Biologie': return 'from-green-500 to-emerald-600';
      case 'Chemie': return 'from-blue-500 to-indigo-600';
      case 'Fyzika': return 'from-pink-500 to-rose-600';
      case 'Somatologie': return 'from-teal-500 to-cyan-600';
      default: return 'from-purple-500 to-violet-600';
    }
  };

  const handleDeleteResult = async (resultId) => {
    if (!window.confirm('Opravdu chcete smazat tento výsledek?')) return;
    try {
      await resultsApi.deleteResult(resultId, user.id);
      loadData();
      setSelectedResult(null);
    } catch (error) {
      console.error('Error deleting result:', error);
    }
  };

  const handlePracticeWrong = async (result) => {
    if (!result.answers) return;
    const wrongAnswers = result.answers.filter(a => a !== null && a !== undefined && !a.isCorrect);
    if (wrongAnswers.length === 0) {
      alert('Nemáte žádné chybné odpovědi k procvičení!');
      return;
    }

    // Načíst otázky podle ID
    const questionIds = wrongAnswers.map(a => a.questionId);
    try {
      const questions = await Promise.all(
        questionIds.map(id => questionsApi.getQuestionById(id))
      );
      setPracticeQuestions({ questions, fromResult: result });
    } catch (error) {
      console.error('Error loading practice questions:', error);
    }
  };

  const handleRemoveFavorite = async (questionId) => {
    try {
      await resultsApi.removeFavorite(user.id, questionId);
      setFavorites(prev => prev.filter(f => f.question?.id !== questionId));
    } catch (error) {
      console.error('Error removing favorite:', error);
    }
  };

  // Handle promo code redemption
  const handleRedeemPromoCode = async (e) => {
    e.preventDefault();
    if (!promoCode.trim() || !user) return;

    setPromoLoading(true);
    setPromoMessage({ type: '', text: '' });

    try {
      const result = await api.redeemPromoCode(promoCode.trim(), user.id);

      if (result.success) {
        setPromoMessage({ type: 'success', text: result.message });
        setPromoCode('');
        // Refresh subscription status
        if (checkSubscription) {
          await checkSubscription(user.id);
        }
      } else {
        setPromoMessage({ type: 'error', text: result.error || 'Nepodařilo se aktivovat kód' });
      }
    } catch (error) {
      console.error('Error redeeming promo code:', error);
      setPromoMessage({ type: 'error', text: 'Chyba při aktivaci kódu' });
    } finally {
      setPromoLoading(false);
    }
  };

  const cards = [
    {
      id: 'results',
      name: 'Moje výsledky',
      icon: Trophy,
      color: 'from-amber-500 to-orange-600',
      description: 'Historie testů a procvičování'
    },
    {
      id: 'statistics',
      name: 'Statistiky',
      icon: BarChart3,
      color: 'from-purple-500 to-violet-600',
      description: 'Analýza a doporučení'
    },
    {
      id: 'favorites',
      name: 'Oblíbené',
      icon: Heart,
      color: 'from-red-500 to-pink-600',
      description: 'Uložené otázky'
    },
    {
      id: 'simulations',
      name: 'Simulace přijímaček',
      icon: GraduationCap,
      color: 'from-cyan-500 to-blue-600',
      description: 'Výsledky simulací'
    }
  ];

  // Practice Mode Component
  if (practiceQuestions) {
    return (
      <PracticeMode
        questions={practiceQuestions.questions}
        onClose={() => setPracticeQuestions(null)}
        onFinish={() => {
          setPracticeQuestions(null);
          loadData();
        }}
      />
    );
  }

  // Result Detail Modal
  const ResultDetailModal = ({ result, onClose }) => {
    if (!result) return null;

    const validAnswers = result.answers?.filter(a => a !== null && a !== undefined) || [];
    const wrongCount = validAnswers.filter(a => !a.isCorrect).length;
    const correctCount = result.score;

    return (
      <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 overflow-y-auto">
        <div className="glass rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 glass p-4 border-b border-dark-700 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                {result.category || 'Simulace přijímaček'}
              </h2>
              <p className="text-dark-400 text-sm">{formatDate(result.created_at)}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => handlePracticeWrong(result)}
                disabled={wrongCount === 0}
                className={`px-4 py-2 rounded-xl flex items-center gap-2 text-sm font-medium transition-colors ${
                  wrongCount > 0
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'bg-dark-700 text-dark-500 cursor-not-allowed'
                }`}
              >
                <RotateCcw size={16} />
                Procvičit chyby ({wrongCount})
              </button>
              <button onClick={onClose} className="p-2 hover:bg-dark-700 rounded-lg">
                <X size={20} className="text-dark-400" />
              </button>
            </div>
          </div>

          <div className="p-6">
            {/* Score Summary */}
            {result.type === 'simulation' ? (
              // Simulace - zobrazit body
              <div className="grid grid-cols-5 gap-3 mb-6">
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-green-400">{correctCount}</p>
                  <p className="text-dark-400 text-xs">Správně</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-red-400">{wrongCount}</p>
                  <p className="text-dark-400 text-xs">Špatně</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-amber-400">
                    {validAnswers.reduce((sum, a) => sum + (a.isCorrect ? (a.scoreValue || 1) : 0), 0)}/
                    {validAnswers.reduce((sum, a) => sum + (a.scoreValue || 1), 0)}
                  </p>
                  <p className="text-dark-400 text-xs">Body</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">{result.percentage}%</p>
                  <p className="text-dark-400 text-xs">Úspěšnost</p>
                </div>
                <div className="glass rounded-xl p-3 text-center">
                  <p className="text-xl font-bold text-white">{formatTime(result.time_seconds)}</p>
                  <p className="text-dark-400 text-xs">Čas</p>
                </div>
              </div>
            ) : (
              // Quiz - bez bodů
              <div className="grid grid-cols-4 gap-4 mb-6">
                <div className="glass rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-green-400">{correctCount}</p>
                  <p className="text-dark-400 text-sm">Správně</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-red-400">{wrongCount}</p>
                  <p className="text-dark-400 text-sm">Špatně</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">{result.percentage}%</p>
                  <p className="text-dark-400 text-sm">Úspěšnost</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">{formatTime(result.time_seconds)}</p>
                  <p className="text-dark-400 text-sm">Čas</p>
                </div>
              </div>
            )}

            {/* Questions - kompaktní zobrazení */}
            <div className="space-y-2">
              {validAnswers.map((answer, index) => (
                <div
                  key={index}
                  className={`glass rounded-lg p-3 border-l-3 ${
                    answer.isCorrect ? 'border-l-green-500' : 'border-l-red-500'
                  }`}
                  style={{ borderLeftWidth: '3px' }}
                >
                  {/* Otázka */}
                  <div className="flex items-start gap-2 mb-2">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                      answer.isCorrect ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {index + 1}
                    </span>
                    <p className="text-white text-sm flex-1">{answer.questionText || `Otázka ${index + 1}`}</p>
                    {/* Body pro simulaci */}
                    {result.type === 'simulation' && answer.scoreValue && (
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        answer.isCorrect ? 'bg-green-500/20 text-green-400' : 'bg-dark-700 text-dark-400'
                      }`}>
                        {answer.isCorrect ? `+${answer.scoreValue}` : `0/${answer.scoreValue}`} b
                      </span>
                    )}
                  </div>

                  {/* Odpovědi - pouze tvá odpověď a správná */}
                  <div className="ml-7 space-y-1">
                    {/* Tvá odpověď (pokud byla špatná) */}
                    {!answer.isCorrect && answer.options && (
                      <div className="flex items-center gap-2 text-xs">
                        <X size={12} className="text-red-400" />
                        <span className="text-red-400">{answer.options[answer.selected]}</span>
                      </div>
                    )}
                    {/* Správná odpověď */}
                    {answer.options && (
                      <div className="flex items-center gap-2 text-xs">
                        <Check size={12} className="text-green-400" />
                        <span className="text-green-400">{answer.options[answer.correct]}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      {/* Background Effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Ahoj{user?.email ? `, ${user.email.split('@')[0]}` : ''}!
          </h1>
          <p className="text-dark-400">
            {hasActiveSubscription
              ? 'Vítejte zpět. Pojďme pokračovat v učení.'
              : 'Pro přístup ke všem funkcím si aktivujte předplatné.'}
          </p>
        </div>

        {/* Subscription Status */}
        {hasActiveSubscription ? (
          <div className="space-y-3 mb-8">
            {(subscription?.subscriptions || [subscription?.subscription]).filter(Boolean).map((sub, idx) => {
              const planInfo = PLAN_LABELS[sub.plan] || { name: sub.plan.replace(/_/g, ' '), detail: '', color: 'text-primary-400' };
              const expiresDate = sub.expires_at ? new Date(sub.expires_at) : null;
              const daysLeft = expiresDate ? Math.ceil((expiresDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
              return (
              <div key={sub.stripe_subscription_id || idx} className="glass rounded-2xl p-6 border border-primary-500/30">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-primary-500/20">
                      <Crown size={24} className="text-primary-400" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h2 className="text-lg font-semibold text-white">{planInfo.name}</h2>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-dark-700 ${planInfo.color}`}>{planInfo.detail}</span>
                      </div>
                      {expiresDate && (
                        <p className="text-dark-400 text-sm flex items-center gap-1 mt-1">
                          <Clock size={14} />
                          <span>Platné do: <span className="text-white font-medium">{expiresDate.toLocaleDateString('cs-CZ')}</span></span>
                          {daysLeft !== null && daysLeft <= 14 && (
                            <span className="text-amber-400 font-medium ml-1">({daysLeft} dní zbývá)</span>
                          )}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      const customerId = sub.stripe_customer_id;
                      api.createPortalSession(customerId, user?.email).then(({ url, error }) => {
                        if (url) {
                          window.location.href = url;
                        } else {
                          alert('Nepodařilo se otevřít správu předplatného. Kontaktujte podporu na info@synapso.cz');
                        }
                      }).catch(() => {
                        alert('Nepodařilo se otevřít správu předplatného. Kontaktujte podporu na info@synapso.cz');
                      });
                    }}
                    className="px-6 py-2 rounded-xl bg-dark-800 text-white font-medium hover:bg-dark-700 transition-colors flex items-center space-x-2 shrink-0"
                  >
                    <Settings size={18} />
                    <span>Spravovat / Zrušit</span>
                  </button>
                </div>
              </div>
              );
            })}
          </div>
        ) : (
          <div className="glass rounded-2xl p-6 mb-8 border border-amber-500/30">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-500/20">
                  <Crown size={24} className="text-amber-400" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Žádné aktivní předplatné</h2>
                  <p className="text-dark-400 text-sm">Aktivujte si předplatné a získejte přístup ke všem funkcím.</p>
                </div>
              </div>
              <Link
                to="/pricing"
                className="px-6 py-2 rounded-xl gradient-bg text-white font-medium btn-hover flex items-center space-x-2"
              >
                <span>Aktivovat předplatné</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        )}

        {/* Cards Grid */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">Přehled</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {cards.map((card) => (
              <button
                key={card.id}
                onClick={() => hasActiveSubscription && setActiveCard(activeCard === card.id ? null : card.id)}
                disabled={!hasActiveSubscription}
                className={`glass rounded-2xl p-6 text-left transition-all ${
                  hasActiveSubscription
                    ? 'cursor-pointer hover:ring-2 hover:ring-primary-500/50'
                    : 'opacity-50 cursor-not-allowed'
                } ${activeCard === card.id ? 'ring-2 ring-primary-500' : ''}`}
              >
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center mb-4`}>
                  <card.icon size={24} className="text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-1">{card.name}</h3>
                <p className="text-dark-400 text-sm mb-4">{card.description}</p>
                {hasActiveSubscription && (
                  <div className="flex items-center text-primary-400 text-sm font-medium">
                    <span>{activeCard === card.id ? 'Zavřít' : 'Zobrazit'}</span>
                    {activeCard === card.id ? (
                      <ChevronDown size={14} className="ml-1" />
                    ) : (
                      <ChevronRight size={14} className="ml-1" />
                    )}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Expanded Card Content */}
        {activeCard && hasActiveSubscription && (
          <div className="glass rounded-2xl p-6 mb-8 animate-fadeIn">
            {activeCard === 'results' && (
              <ResultsSection
                results={filteredResults}
                onViewDetail={setSelectedResult}
                onPractice={handlePracticeWrong}
                onDelete={handleDeleteResult}
                formatDate={formatDate}
                formatTime={formatTime}
                getCategoryIcon={getCategoryIcon}
                getCategoryColor={getCategoryColor}
                timeFilter={timeFilter}
                setTimeFilter={setTimeFilter}
              />
            )}
            {activeCard === 'statistics' && (
              <StatisticsSection
                stats={filteredStats}
                formatTime={formatTime}
                getCategoryColor={getCategoryColor}
                timeFilter={timeFilter}
                setTimeFilter={setTimeFilter}
              />
            )}
            {activeCard === 'favorites' && (
              <FavoritesSection
                favorites={favorites}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                onRemove={handleRemoveFavorite}
                getCategoryIcon={getCategoryIcon}
                getCategoryColor={getCategoryColor}
              />
            )}
            {activeCard === 'simulations' && (
              <SimulationsSection
                simulations={filteredSimulations}
                stats={stats}
                onViewDetail={setSelectedResult}
                formatDate={formatDate}
                formatTime={formatTime}
                navigate={navigate}
                timeFilter={timeFilter}
                setTimeFilter={setTimeFilter}
              />
            )}
          </div>
        )}

        {/* Account Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <User size={20} className="text-primary-400" />
                <h3 className="text-lg font-semibold text-white">Informace o účtu</h3>
              </div>
              <button
                onClick={() => setShowProfileModal(true)}
                className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                title="Upravit účet"
              >
                <Settings size={18} className="text-dark-400 hover:text-primary-400" />
              </button>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-dark-400">Email</span>
                <span className="text-white">{user?.email}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">ID účtu</span>
                <span className="text-white text-sm">{user?.id?.slice(0, 8)}...</span>
              </div>
              <div className="flex justify-between">
                <span className="text-dark-400">Registrace</span>
                <span className="text-white">
                  {user?.created_at
                    ? new Date(user.created_at).toLocaleDateString('cs-CZ')
                    : '-'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowProfileModal(true)}
              className="w-full mt-4 px-4 py-2 rounded-xl bg-dark-700 text-dark-300 hover:bg-dark-600 hover:text-white transition-colors text-sm font-medium"
            >
              Upravit nastavení účtu
            </button>
          </div>

          <div className="glass rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <CreditCard size={20} className="text-primary-400" />
              <h3 className="text-lg font-semibold text-white">Předplatné</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-dark-400">Stav</span>
                <span className={`flex items-center space-x-1 ${hasActiveSubscription ? 'text-green-400' : 'text-amber-400'}`}>
                  {hasActiveSubscription ? (
                    <>
                      <CheckCircle size={14} />
                      <span>Aktivní</span>
                    </>
                  ) : (
                    <span>Neaktivní</span>
                  )}
                </span>
              </div>
              {hasActiveSubscription && (subscription?.subscriptions || [subscription?.subscription]).filter(Boolean).map((sub, idx) => (
                <div key={idx} className="border-t border-dark-700 pt-3 space-y-1">
                  <div className="flex justify-between">
                    <span className="text-dark-400">Plán</span>
                    <span className="text-white">{sub.plan.replace(/_/g, ' ')}</span>
                  </div>
                  {sub.expires_at && (
                    <div className="flex justify-between">
                      <span className="text-dark-400">Platné do</span>
                      <span className="text-white">{new Date(sub.expires_at).toLocaleDateString('cs-CZ')}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Promo Code Section */}
          <div className="glass rounded-2xl p-6">
            <div className="flex items-center space-x-3 mb-4">
              <Gift size={20} className="text-primary-400" />
              <h3 className="text-lg font-semibold text-white">Promo kód</h3>
            </div>
            <p className="text-dark-400 text-sm mb-4">
              Máte promo kód z knihy? Zadejte jej zde pro aktivaci prémiového přístupu.
            </p>
            <form onSubmit={handleRedeemPromoCode} className="space-y-3">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="BCHF-XXXXX"
                className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none font-mono tracking-wider"
                maxLength={10}
              />
              {promoMessage.text && (
                <div className={`p-3 rounded-lg text-sm ${
                  promoMessage.type === 'error'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  {promoMessage.text}
                </div>
              )}
              <button
                type="submit"
                disabled={promoLoading || !promoCode.trim()}
                className="w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {promoLoading ? 'Aktivuji...' : 'Aktivovat kód'}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Result Detail Modal */}
      {selectedResult && (
        <ResultDetailModal result={selectedResult} onClose={() => setSelectedResult(null)} />
      )}

      {/* Profile Settings Modal */}
      {showProfileModal && (
        <ProfileModal
          user={user}
          updatePassword={updatePassword}
          onClose={() => setShowProfileModal(false)}
        />
      )}
    </div>
  );
};

// Profile Settings Modal Component
const ProfileModal = ({ user, updatePassword, onClose }) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [loading, setLoading] = useState(false);

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      setMessage({ type: 'error', text: 'Hesla se neshodují' });
      return;
    }

    if (newPassword.length < 6) {
      setMessage({ type: 'error', text: 'Heslo musí mít alespoň 6 znaků' });
      return;
    }

    setLoading(true);
    try {
      await updatePassword(newPassword);
      setMessage({ type: 'success', text: 'Heslo bylo úspěšně změněno' });
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setMessage({ type: 'error', text: error.message || 'Chyba při změně hesla' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[100] flex items-center justify-center p-4">
      <div className="glass rounded-2xl max-w-lg w-full max-h-[90vh] overflow-hidden animate-fadeIn">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-dark-700">
          <h2 className="text-xl font-bold text-white">Nastavení účtu</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
          >
            <X size={20} className="text-dark-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-dark-700">
          <button
            onClick={() => setActiveTab('profile')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'profile'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-dark-400 hover:text-white'
            }`}
          >
            Profil
          </button>
          <button
            onClick={() => setActiveTab('security')}
            className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'security'
                ? 'text-primary-400 border-b-2 border-primary-400'
                : 'text-dark-400 hover:text-white'
            }`}
          >
            Zabezpečení
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'profile' && (
            <div className="space-y-6">
              {/* Avatar */}
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                  <span className="text-white font-bold text-2xl">
                    {user?.email?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-white font-medium">{user?.email?.split('@')[0]}</p>
                  <p className="text-dark-400 text-sm">{user?.email}</p>
                </div>
              </div>

              {/* Profile Info */}
              <div className="space-y-4">
                <div>
                  <label className="block text-dark-400 text-sm mb-2">Email</label>
                  <input
                    type="email"
                    value={user?.email || ''}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-dark-800 text-dark-400 border border-dark-700 cursor-not-allowed"
                  />
                  <p className="text-dark-500 text-xs mt-1">Email nelze změnit</p>
                </div>

                <div>
                  <label className="block text-dark-400 text-sm mb-2">ID účtu</label>
                  <input
                    type="text"
                    value={user?.id || ''}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-dark-800 text-dark-400 border border-dark-700 cursor-not-allowed font-mono text-sm"
                  />
                </div>

                <div>
                  <label className="block text-dark-400 text-sm mb-2">Datum registrace</label>
                  <input
                    type="text"
                    value={user?.created_at ? new Date(user.created_at).toLocaleDateString('cs-CZ') : '-'}
                    disabled
                    className="w-full px-4 py-3 rounded-xl bg-dark-800 text-dark-400 border border-dark-700 cursor-not-allowed"
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === 'security' && (
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-dark-400 text-sm mb-2">Nové heslo</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Zadejte nové heslo"
                  className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-dark-400 text-sm mb-2">Potvrdit nové heslo</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Potvrďte nové heslo"
                  className="w-full px-4 py-3 rounded-xl bg-dark-800 text-white border border-dark-700 focus:border-primary-500 focus:outline-none"
                />
              </div>

              {message.text && (
                <div className={`p-3 rounded-lg text-sm ${
                  message.type === 'error'
                    ? 'bg-red-500/20 text-red-400'
                    : 'bg-green-500/20 text-green-400'
                }`}>
                  {message.text}
                </div>
              )}

              <button
                type="submit"
                disabled={loading || !newPassword || !confirmPassword}
                className="w-full px-4 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Ukládám...' : 'Změnit heslo'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

// Time Filter Component
const TimeFilter = ({ value, onChange }) => {
  const options = [
    { value: 'all', label: 'Vše' },
    { value: '7days', label: '7 dní' },
    { value: '30days', label: '30 dní' },
    { value: '90days', label: '90 dní' },
  ];

  return (
    <div className="flex gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          onClick={() => onChange(option.value)}
          className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
            value === option.value
              ? 'bg-primary-500 text-white'
              : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

// Results Section Component
const ResultsSection = ({ results, onViewDetail, onPractice, onDelete, formatDate, formatTime, getCategoryIcon, getCategoryColor, timeFilter, setTimeFilter }) => {
  if (results.length === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Historie testů</h3>
          <TimeFilter value={timeFilter} onChange={setTimeFilter} />
        </div>
        <div className="text-center py-12">
          <Trophy size={48} className="mx-auto text-dark-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Žádné výsledky v tomto období</h3>
          <p className="text-dark-400">Změňte filtr nebo dokončete test.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Historie testů ({results.length})</h3>
        <TimeFilter value={timeFilter} onChange={setTimeFilter} />
      </div>
      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        {results.map((result) => {
          const Icon = getCategoryIcon(result.category);
          const wrongCount = result.answers?.filter(a => a && !a.isCorrect).length || 0;

          return (
            <div key={result.id} className="glass rounded-xl p-4 hover:bg-dark-800/50 transition-colors">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${getCategoryColor(result.category)} flex items-center justify-center`}>
                    <Icon size={20} className="text-white" />
                  </div>
                  <div>
                    <h4 className="text-white font-medium">{result.category}</h4>
                    <p className="text-dark-400 text-sm">{formatDate(result.created_at)}</p>
                  </div>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className={`text-lg font-bold ${result.percentage >= 70 ? 'text-green-400' : result.percentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                      {result.percentage}%
                    </p>
                    <p className="text-dark-400 text-sm">
                      {result.score}/{result.total_questions}
                      {(result.total_score !== null && result.max_score !== null) ? (
                        <span className="ml-2 text-amber-400">
                          ({result.total_score}/{result.max_score} b)
                        </span>
                      ) : result.answers && result.answers.filter(a => a).some(a => a.scoreValue > 1) && (
                        <span className="ml-2 text-amber-400">
                          ({result.answers.filter(a => a && a.isCorrect).reduce((sum, a) => sum + (a.scoreValue || 1), 0)}/{result.answers.filter(a => a).reduce((sum, a) => sum + (a.scoreValue || 1), 0)} b)
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onViewDetail(result)}
                      className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                      title="Zobrazit detail"
                    >
                      <Eye size={18} className="text-dark-400 hover:text-white" />
                    </button>
                    {wrongCount > 0 && (
                      <button
                        onClick={() => onPractice(result)}
                        className="p-2 hover:bg-primary-500/20 rounded-lg transition-colors"
                        title={`Procvičit ${wrongCount} chyb`}
                      >
                        <RotateCcw size={18} className="text-primary-400" />
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(result.id)}
                      className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                      title="Smazat"
                    >
                      <Trash2 size={18} className="text-dark-400 hover:text-red-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Statistics Section Component
const StatisticsSection = ({ stats, formatTime, getCategoryColor, timeFilter, setTimeFilter }) => {
  if (!stats || stats.totalTests === 0) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-white">Vaše statistiky</h3>
          <TimeFilter value={timeFilter} onChange={setTimeFilter} />
        </div>
        <div className="text-center py-12">
          <BarChart3 size={48} className="mx-auto text-dark-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Žádné statistiky v tomto období</h3>
          <p className="text-dark-400">Změňte filtr nebo dokončete test.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Vaše statistiky</h3>
        <TimeFilter value={timeFilter} onChange={setTimeFilter} />
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-primary-400">{stats.totalTests}</p>
          <p className="text-dark-400 text-sm">Celkem testů</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-white">{stats.totalQuestions}</p>
          <p className="text-dark-400 text-sm">Otázek</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className={`text-3xl font-bold ${stats.averagePercentage >= 70 ? 'text-green-400' : stats.averagePercentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
            {stats.averagePercentage}%
          </p>
          <p className="text-dark-400 text-sm">Průměr</p>
        </div>
        <div className="glass rounded-xl p-4 text-center">
          <p className="text-3xl font-bold text-white">{formatTime(stats.totalTime)}</p>
          <p className="text-dark-400 text-sm">Celkový čas</p>
        </div>
      </div>

      {/* Category Breakdown */}
      <div className="mb-8">
        <h4 className="text-white font-medium mb-4 flex items-center gap-2">
          <Target size={18} className="text-primary-400" />
          Úspěšnost podle předmětu
        </h4>
        <div className="space-y-3">
          {Object.entries(stats.byCategory).map(([category, data]) => (
            <div key={category} className="glass rounded-xl p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-white font-medium">{category}</span>
                <span className={`font-bold ${data.percentage >= 70 ? 'text-green-400' : data.percentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                  {data.percentage}%
                </span>
              </div>
              <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${getCategoryColor(category)}`}
                  style={{ width: `${data.percentage}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-dark-400 text-sm">
                <span>{data.correct}/{data.total} správně</span>
                <span>{data.tests} testů</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recommendations */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Weak Areas */}
        {stats.weakCategories.length > 0 && (
          <div className="glass rounded-xl p-4 border border-red-500/30">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <AlertCircle size={18} className="text-red-400" />
              Doporučeno procvičit
            </h4>
            <div className="space-y-2">
              {stats.weakCategories.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between">
                  <span className="text-dark-300">{cat.name}</span>
                  <span className="text-red-400 font-medium">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Strong Areas */}
        {stats.strongCategories.length > 0 && (
          <div className="glass rounded-xl p-4 border border-green-500/30">
            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
              <TrendingUp size={18} className="text-green-400" />
              Vaše silné stránky
            </h4>
            <div className="space-y-2">
              {stats.strongCategories.map((cat) => (
                <div key={cat.name} className="flex items-center justify-between">
                  <span className="text-dark-300">{cat.name}</span>
                  <span className="text-green-400 font-medium">{cat.percentage}%</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Favorites Section Component
const FavoritesSection = ({ favorites, selectedCategory, setSelectedCategory, onRemove, getCategoryIcon, getCategoryColor }) => {
  const categories = ['all', 'Biologie', 'Chemie', 'Fyzika', 'Somatologie'];

  const filteredFavorites = selectedCategory === 'all'
    ? favorites
    : favorites.filter(f => f.question?.category === selectedCategory);

  if (favorites.length === 0) {
    return (
      <div className="text-center py-12">
        <Heart size={48} className="mx-auto text-dark-600 mb-4" />
        <h3 className="text-lg font-semibold text-white mb-2">Zatím žádné oblíbené</h3>
        <p className="text-dark-400">Přidejte si otázky do oblíbených kliknutím na srdíčko.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Oblíbené otázky ({favorites.length})</h3>
        <div className="flex gap-2">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3 py-1 rounded-lg text-sm transition-colors ${
                selectedCategory === cat
                  ? 'bg-primary-500 text-white'
                  : 'bg-dark-700 text-dark-300 hover:bg-dark-600'
              }`}
            >
              {cat === 'all' ? 'Vše' : cat}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-3 max-h-96 overflow-y-auto pr-2">
        {filteredFavorites.map((fav) => {
          const question = fav.question;
          if (!question) return null;
          const Icon = getCategoryIcon(question.category);

          return (
            <div key={fav.id} className="glass rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${getCategoryColor(question.category)} flex items-center justify-center`}>
                      <Icon size={16} className="text-white" />
                    </div>
                    <span className="text-dark-400 text-sm">{question.category} #{question.question_number}</span>
                  </div>
                  <p className="text-white">{question.question_text}</p>
                  {question.image_name && (
                    <img
                      src={getImageUrl(question.image_name)}
                      alt=""
                      className="max-h-24 rounded-lg mt-2"
                    />
                  )}
                </div>
                <button
                  onClick={() => onRemove(question.id)}
                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
                >
                  <Heart size={18} className="text-red-400 fill-red-400" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Simulations Section Component
const SimulationsSection = ({ simulations, stats, onViewDetail, formatDate, formatTime, navigate, timeFilter, setTimeFilter }) => {
  // Spočítat statistiky z filtrovaných simulací
  const avgPercentage = simulations.length > 0
    ? Math.round(simulations.reduce((sum, s) => sum + s.percentage, 0) / simulations.length)
    : 0;
  const bestScore = simulations.length > 0
    ? Math.max(...simulations.map(s => s.percentage))
    : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">Simulace přijímaček</h3>
        <div className="flex items-center gap-4">
          <TimeFilter value={timeFilter} onChange={setTimeFilter} />
          <button
            onClick={() => navigate('/simulation')}
            className="px-4 py-2 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors flex items-center gap-2"
          >
            <Play size={18} />
            Nová simulace
          </button>
        </div>
      </div>

      {simulations.length === 0 ? (
        <div className="text-center py-12">
          <GraduationCap size={48} className="mx-auto text-dark-600 mb-4" />
          <h3 className="text-lg font-semibold text-white mb-2">Žádné simulace v tomto období</h3>
          <p className="text-dark-400 mb-4">Změňte filtr nebo vyzkoušejte novou simulaci.</p>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="glass rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-primary-400">{simulations.length}</p>
              <p className="text-dark-400 text-sm">Simulací</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <p className={`text-3xl font-bold ${avgPercentage >= 70 ? 'text-green-400' : avgPercentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                {avgPercentage}%
              </p>
              <p className="text-dark-400 text-sm">Průměr</p>
            </div>
            <div className="glass rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-green-400">{bestScore}%</p>
              <p className="text-dark-400 text-sm">Nejlepší</p>
            </div>
          </div>

          {/* History */}
          <div className="space-y-3 max-h-64 overflow-y-auto pr-2">
            {simulations.map((sim) => (
              <div
                key={sim.id}
                className="glass rounded-xl p-4 hover:bg-dark-800/50 transition-colors cursor-pointer"
                onClick={() => onViewDetail(sim)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                      <GraduationCap size={20} className="text-white" />
                    </div>
                    <div>
                      <h4 className="text-white font-medium">Simulace přijímaček</h4>
                      <p className="text-dark-400 text-sm">{formatDate(sim.created_at)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className={`text-lg font-bold ${sim.percentage >= 70 ? 'text-green-400' : sim.percentage >= 50 ? 'text-amber-400' : 'text-red-400'}`}>
                        {sim.percentage}%
                      </p>
                      <p className="text-dark-400 text-sm">
                        {sim.score}/{sim.total_questions} • {formatTime(sim.time_seconds)}
                        {(sim.total_score !== null && sim.max_score !== null) ? (
                          <span className="ml-2 text-amber-400">
                            ({sim.total_score}/{sim.max_score} b)
                          </span>
                        ) : sim.answers && (
                          <span className="ml-2 text-amber-400">
                            ({sim.answers.filter(a => a?.isCorrect).reduce((sum, a) => sum + (a?.scoreValue || 1), 0)}/{sim.answers.reduce((sum, a) => sum + (a?.scoreValue || 1), 0)} b)
                          </span>
                        )}
                      </p>
                    </div>
                    <ChevronRight size={18} className="text-dark-400" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

// Practice Mode Component
const PracticeMode = ({ questions, onClose, onFinish }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [score, setScore] = useState(0);
  const [finished, setFinished] = useState(false);

  const currentQuestion = questions[currentIndex];

  const handleAnswer = (index) => {
    if (selectedAnswer !== null) return;
    setSelectedAnswer(index);
    if (index === currentQuestion.correct_answer_index) {
      setScore(prev => prev + 1);
    }
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setSelectedAnswer(null);
      setShowExplanation(false);
    } else {
      setFinished(true);
    }
  };

  if (finished) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12 flex items-center justify-center">
        <div className="glass rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center mb-6">
            <Sparkles size={32} className="text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Procvičování dokončeno!</h2>
          <p className="text-dark-400 mb-6">
            Správně jste odpověděli na {score} z {questions.length} otázek.
          </p>
          <button
            onClick={onFinish}
            className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors"
          >
            Zpět na Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={onClose}
            className="flex items-center gap-2 text-dark-400 hover:text-white transition-colors"
          >
            <X size={20} />
            <span>Ukončit</span>
          </button>
          <div className="text-white font-medium">
            {currentIndex + 1} / {questions.length}
          </div>
        </div>

        <div className="w-full h-2 bg-dark-800 rounded-full mb-8 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-primary-500 to-purple-500 rounded-full transition-all"
            style={{ width: `${((currentIndex + 1) / questions.length) * 100}%` }}
          />
        </div>

        <div className="glass rounded-2xl p-6 mb-6">
          <span className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-400 text-sm font-medium">
            {currentQuestion.category}
          </span>
          <h2 className="text-xl font-semibold text-white mt-4 mb-6">
            {currentQuestion.question_text}
          </h2>

          {currentQuestion.image_name && (
            <img
              src={getImageUrl(currentQuestion.image_name)}
              alt=""
              className="max-w-full max-h-64 rounded-xl mb-6 mx-auto"
            />
          )}

          <div className="space-y-3">
            {currentQuestion.options.map((option, index) => {
              const isSelected = selectedAnswer === index;
              const isCorrect = index === currentQuestion.correct_answer_index;
              const showResult = selectedAnswer !== null;

              let buttonClass = 'glass';
              if (showResult) {
                if (isCorrect) {
                  buttonClass = 'bg-green-500/20 border border-green-500';
                } else if (isSelected && !isCorrect) {
                  buttonClass = 'bg-red-500/20 border border-red-500';
                }
              }

              return (
                <button
                  key={index}
                  onClick={() => handleAnswer(index)}
                  disabled={selectedAnswer !== null}
                  className={`w-full p-4 rounded-xl text-left transition-all ${buttonClass} ${
                    selectedAnswer === null ? 'hover:bg-dark-700' : ''
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-white">{option}</span>
                    {showResult && isCorrect && <Check size={20} className="text-green-400" />}
                    {showResult && isSelected && !isCorrect && <X size={20} className="text-red-400" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {showExplanation && currentQuestion.explanation && (
          <div className="glass rounded-2xl p-6 mb-6 border border-primary-500/30">
            <h3 className="text-lg font-semibold text-white mb-2">Vysvětlení</h3>
            <p className="text-dark-300">{currentQuestion.explanation}</p>
          </div>
        )}

        {selectedAnswer !== null && (
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              className="px-6 py-3 rounded-xl bg-primary-500 text-white font-semibold hover:bg-primary-600 transition-colors flex items-center gap-2"
            >
              <span>{currentIndex < questions.length - 1 ? 'Další otázka' : 'Dokončit'}</span>
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
